/**
 * FOLDER STRUCTURE SERVICE - Servicio genérico de estructura de carpetas
 * 
 * Proporciona CRUD de carpetas y gestión de jerarquía para cualquier
 * entidad que necesite organización en carpetas.
 * 
 * Características:
 * - Carpetas anidadas ilimitadas
 * - Path materializado para queries eficientes
 * - Conteos desnormalizados para UI rápida
 * - Extensible con metadata personalizada
 */

import { db } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import {
    Folder,
    FolderTreeNode,
    FolderBreadcrumb,
    CreateFolderDTO,
    UpdateFolderDTO,
    GetFoldersOptions,
    DeleteFolderOptions,
    OwnerType,
    FOLDERS_COLLECTION,
} from './folder-structure.types';

const logger = Logger.getLogger('FolderStructureService');

class FolderStructureService {
    private collection = db.collection(FOLDERS_COLLECTION);

    // ============================================
    // CRUD DE CARPETAS
    // ============================================

    async createFolder(
        ownerId: string,
        ownerType: OwnerType,
        data: CreateFolderDTO
    ): Promise<Folder> {
        const now = new Date().toISOString();

        // Calcular path y depth basado en padre
        let path: string[] = [];
        let depth = 0;

        if (data.parentId) {
            const parent = await this.getFolder(data.parentId);
            if (!parent) throw new Error('Carpeta padre no encontrada');
            if (parent.ownerId !== ownerId) throw new Error('No puedes crear subcarpetas en carpetas de otros');
            path = [...parent.path, parent.id];
            depth = parent.depth + 1;
        }

        // Obtener orden máximo para esta ubicación
        const order = await this.getNextOrder(ownerId, ownerType, data.parentId);

        const folderData: Omit<Folder, 'id'> = {
            ownerId,
            ownerType,
            name: data.name.trim(),
            description: data.description,
            icon: data.icon || '📁',
            color: data.color,
            parentId: data.parentId,
            path,
            depth,
            order,
            itemsCount: 0,
            totalItemsCount: 0,
            subfolderCount: 0,
            metadata: data.metadata,
            createdAt: now,
            updatedAt: now,
        };

        // Limpiar undefined para Firestore
        const cleanData = Object.fromEntries(
            Object.entries(folderData).filter(([_, v]) => v !== undefined)
        );

        const docRef = await this.collection.add(cleanData);

        // Actualizar contador de subcarpetas del padre
        if (data.parentId) {
            await this.incrementSubfolderCount(data.parentId, 1);
        }

        logger.info('Folder created', { folderId: docRef.id, name: data.name, ownerType });

        return { id: docRef.id, ...folderData };
    }

    async getFolder(folderId: string): Promise<Folder | null> {
        const doc = await this.collection.doc(folderId).get();
        if (!doc.exists) return null;
        return { id: doc.id, ...doc.data() } as Folder;
    }

    async getFolders(
        ownerId: string,
        ownerType: OwnerType,
        options?: GetFoldersOptions
    ): Promise<Folder[]> {
        // Base query: solo por owner
        let query: FirebaseFirestore.Query = this.collection
            .where('ownerId', '==', ownerId)
            .where('ownerType', '==', ownerType);

        // Para subcarpetas específicas, filtrar en Firestore
        // Para raíz o sin filtro, obtener todo y filtrar en cliente
        if (options?.parentId !== undefined && options.parentId !== null) {
            query = query.where('parentId', '==', options.parentId);
        }

        // Obtener datos
        const snapshot = await query.get();
        
        let folders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Folder));
        
        // Filtrar por parentId en cliente (para carpetas raíz)
        // Firestore no puede comparar null con campos inexistentes correctamente
        if (options?.parentId === null) {
            folders = folders.filter(f => !f.parentId);
        }
        
        // Ordenar en cliente
        const sortBy = options?.sortBy || 'order';
        const sortDir = options?.sortDir || 'asc';

        folders = folders.sort((a, b) => {
            const aVal = a[sortBy as keyof Folder];
            const bVal = b[sortBy as keyof Folder];
            if (aVal === bVal) return 0;
            if (aVal === undefined || aVal === null) return 1;
            if (bVal === undefined || bVal === null) return -1;
            const comparison = aVal < bVal ? -1 : 1;
            return sortDir === 'asc' ? comparison : -comparison;
        });

        // Filtrar vacías si es necesario
        if (options?.includeEmpty === false) {
            folders = folders.filter(f => f.itemsCount > 0 || f.subfolderCount > 0);
        }

        return folders;
    }

    async getFolderTree(ownerId: string, ownerType: OwnerType): Promise<FolderTreeNode[]> {
        const allFolders = await this.getFolders(ownerId, ownerType);
        return this.buildTree(allFolders);
    }

    async updateFolder(folderId: string, data: UpdateFolderDTO): Promise<void> {
        const cleanData = Object.fromEntries(
            Object.entries({ ...data, updatedAt: new Date().toISOString() })
                .filter(([_, v]) => v !== undefined)
        );

        await this.collection.doc(folderId).update(cleanData);
        logger.info('Folder updated', { folderId });
    }

    async deleteFolder(
        ownerId: string,
        folderId: string,
        options?: DeleteFolderOptions
    ): Promise<void> {
        const folder = await this.getFolder(folderId);
        if (!folder) throw new Error('Carpeta no encontrada');
        if (folder.ownerId !== ownerId) throw new Error('No puedes eliminar esta carpeta');

        const batch = db.batch();

        // Si tiene subcarpetas y recursive=true, eliminarlas
        if (options?.recursive && folder.subfolderCount > 0) {
            const subfolders = await this.collection
                .where('path', 'array-contains', folderId)
                .get();
            subfolders.docs.forEach(doc => batch.delete(doc.ref));
        }

        // Eliminar la carpeta
        batch.delete(this.collection.doc(folderId));

        await batch.commit();

        // Actualizar contador del padre
        if (folder.parentId) {
            await this.incrementSubfolderCount(folder.parentId, -1);
        }

        logger.info('Folder deleted', { folderId, recursive: options?.recursive });
    }

    // ============================================
    // NAVEGACIÓN Y BREADCRUMBS
    // ============================================

    async getBreadcrumbs(folderId: string): Promise<FolderBreadcrumb[]> {
        const folder = await this.getFolder(folderId);
        if (!folder) return [];

        const breadcrumbs: FolderBreadcrumb[] = [];

        // Obtener ancestros por path
        for (const ancestorId of folder.path) {
            const ancestor = await this.getFolder(ancestorId);
            if (ancestor) {
                breadcrumbs.push({ id: ancestor.id, name: ancestor.name, icon: ancestor.icon });
            }
        }

        // Añadir carpeta actual
        breadcrumbs.push({ id: folder.id, name: folder.name, icon: folder.icon });

        return breadcrumbs;
    }

    // ============================================
    // CONTADORES
    // ============================================

    async incrementItemCount(folderId: string, delta: number): Promise<void> {
        const folder = await this.getFolder(folderId);
        if (!folder) return;

        await this.collection.doc(folderId).update({
            itemsCount: Math.max(0, folder.itemsCount + delta),
            totalItemsCount: Math.max(0, folder.totalItemsCount + delta),
            updatedAt: new Date().toISOString(),
        });

        // Propagar a ancestros
        for (const ancestorId of folder.path) {
            const ancestorDoc = await this.collection.doc(ancestorId).get();
            if (ancestorDoc.exists) {
                const ancestor = ancestorDoc.data() as Folder;
                await this.collection.doc(ancestorId).update({
                    totalItemsCount: Math.max(0, ancestor.totalItemsCount + delta),
                });
            }
        }
    }

    private async incrementSubfolderCount(folderId: string, delta: number): Promise<void> {
        const doc = await this.collection.doc(folderId).get();
        if (!doc.exists) return;

        const folder = doc.data() as Folder;
        await this.collection.doc(folderId).update({
            subfolderCount: Math.max(0, folder.subfolderCount + delta),
            updatedAt: new Date().toISOString(),
        });
    }

    // ============================================
    // UTILIDADES PRIVADAS
    // ============================================

    private async getNextOrder(
        ownerId: string,
        ownerType: OwnerType,
        parentId?: string
    ): Promise<number> {
        try {
            // Solo usar filtro básico por owner para evitar índices compuestos
            const query = this.collection
                .where('ownerId', '==', ownerId)
                .where('ownerType', '==', ownerType);

            const snapshot = await query.get();

            if (snapshot.empty) return 0;
            
            // Filtrar en cliente y encontrar el order máximo
            let maxOrder = 0;
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                // Filtrar por parentId en cliente
                const docParentId = data.parentId;
                const matches = parentId ? docParentId === parentId : !docParentId;
                
                if (matches) {
                    const order = data.order || 0;
                    if (order > maxOrder) maxOrder = order;
                }
            });
            
            return maxOrder + 1;
        } catch (error) {
            logger.warn('Error getting next order, defaulting to 0', { error });
            return 0;
        }
    }

    private buildTree(folders: Folder[]): FolderTreeNode[] {
        const map = new Map<string, FolderTreeNode>();
        const roots: FolderTreeNode[] = [];

        // Crear nodos
        folders.forEach(folder => {
            map.set(folder.id, { ...folder, children: [] });
        });

        // Construir árbol
        folders.forEach(folder => {
            const node = map.get(folder.id)!;
            if (folder.parentId && map.has(folder.parentId)) {
                map.get(folder.parentId)!.children.push(node);
            } else {
                roots.push(node);
            }
        });

        // Ordenar hijos
        const sortChildren = (nodes: FolderTreeNode[]) => {
            nodes.sort((a, b) => a.order - b.order);
            nodes.forEach(n => sortChildren(n.children));
        };
        sortChildren(roots);

        return roots;
    }
}

// Singleton
export const folderStructureService = new FolderStructureService();
